// ============================================================
// PorteriaPage — Registro de asistencia diaria por DNI.
// Foco automático en el input + autosubmit a los 8 dígitos.
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { I } from 'components/ui';
import {
  useAsistenciaDiaria,
  useAsistenciaDiariaPorFecha,
  calcularResumenDiaria,
  todayLima,
} from 'hooks/useAsistenciaDiaria';
import {
  AsistenciaDiariaResponse,
  ESTADO_DIARIA_LABEL,
  ESTADO_DIARIA_COLOR,
  ESTADO_DIARIA_DOT,
} from 'types/asistenciaDiaria.types';

const NIVEL_COLOR: Record<string, string> = {
  INICIAL:    'bg-amber-100 text-amber-700',
  PRIMARIA:   'bg-blue-100 text-blue-700',
  SECUNDARIA: 'bg-purple-100 text-purple-700',
};

const Spinner: React.FC<{ size?: number }> = ({ size = 5 }) => (
  <svg className={`animate-spin w-${size} h-${size}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

const PorteriaPage: React.FC = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dni, setDni] = useState('');
  const [feedback, setFeedback] = useState<{ tipo: 'ok' | 'error'; registro?: AsistenciaDiariaResponse; mensaje?: string } | null>(null);

  const { marcar, marcando } = useAsistenciaDiaria();
  const { registros, loading: loadingLista, recargar } = useAsistenciaDiariaPorFecha(todayLima());

  const enfocarInput = useCallback(() => {
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  useEffect(() => { enfocarInput(); }, [enfocarInput]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Solo dígitos, máx 8
    const limpio = e.target.value.replace(/\D/g, '').slice(0, 8);
    setDni(limpio);
    if (feedback?.tipo === 'error') setFeedback(null);
  };

  const enviar = useCallback(async (valor: string) => {
    if (!/^\d{8}$/.test(valor)) {
      setFeedback({ tipo: 'error', mensaje: 'Ingresa un DNI válido de 8 dígitos.' });
      return;
    }
    try {
      const reg = await marcar({ dni: valor });
      setFeedback({ tipo: 'ok', registro: reg });
      setDni('');
      recargar();
    } catch (err: unknown) {
      setFeedback({ tipo: 'error', mensaje: err instanceof Error ? err.message : 'Error al registrar' });
    } finally {
      enfocarInput();
    }
  }, [marcar, recargar, enfocarInput]);

  // Autosubmit al alcanzar 8 dígitos
  useEffect(() => {
    if (dni.length === 8 && !marcando) {
      enviar(dni);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dni]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (dni && !marcando) enviar(dni);
  };

  const resumen = calcularResumenDiaria(registros);
  const ultimos10 = registros.slice(0, 10);

  return (
    <div className="space-y-6">
      {/* Cabecera */}
      <div>
        <h2 className="text-xl font-bold text-slate-800">Portería — Registro de asistencia</h2>
        <p className="text-slate-400 text-sm mt-0.5">Ingresa el DNI del estudiante para registrar su entrada al colegio</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Columna principal: Input DNI + feedback */}
        <div className="lg:col-span-2 space-y-5">
          <form onSubmit={handleSubmit}
            className="bg-gradient-to-br from-blue-700 to-indigo-800 rounded-2xl p-6 shadow-lg">
            <label className="block text-blue-100 text-xs font-bold uppercase tracking-wide mb-2">
              DNI del estudiante
            </label>
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                pattern="\d{8}"
                placeholder="00000000"
                value={dni}
                onChange={handleChange}
                disabled={marcando}
                autoFocus
                autoComplete="off"
                className="w-full bg-white rounded-xl px-5 py-5 text-3xl font-bold tabular-nums tracking-widest
                           text-slate-800 outline-none focus:ring-4 focus:ring-white/30 disabled:opacity-60"
              />
              {marcando && (
                <div className="absolute right-5 top-1/2 -translate-y-1/2 text-blue-700">
                  <Spinner size={6} />
                </div>
              )}
            </div>
            <p className="text-blue-200 text-xs mt-3">
              Se registra automáticamente al alcanzar 8 dígitos. Compatible con lector de DNI.
            </p>
          </form>

          {/* Feedback */}
          {feedback?.tipo === 'ok' && feedback.registro && (
            <CardEstudiante reg={feedback.registro} />
          )}
          {feedback?.tipo === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
                <I.AlertCircle size={18} />
              </div>
              <div>
                <p className="text-red-700 font-bold text-sm">No se pudo registrar</p>
                <p className="text-red-600 text-sm mt-0.5">{feedback.mensaje}</p>
              </div>
            </div>
          )}
        </div>

        {/* Columna lateral: Contador del día */}
        <ContadorDia
          presentes={resumen.presente}
          tardanzas={resumen.tardanza}
          total={resumen.total}
        />
      </div>

      {/* Últimos 10 registros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">
            Últimos registros del día
          </span>
          <span className="text-xs text-slate-400">{registros.length} en total</span>
        </div>
        {loadingLista && registros.length === 0 ? (
          <div className="flex justify-center py-10 text-blue-700"><Spinner size={6} /></div>
        ) : ultimos10.length === 0 ? (
          <p className="text-slate-400 text-sm py-10 text-center">Aún no hay registros hoy</p>
        ) : (
          <ul className="divide-y divide-slate-50">
            {ultimos10.map(r => (
              <li key={r.idAsistenciaDiaria} className="px-5 py-3 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {r.estudianteNombres.charAt(0)}{r.estudianteApellidos.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800 truncate">
                    {r.estudianteApellidos}, {r.estudianteNombres}
                  </p>
                  <p className="text-xs text-slate-400">
                    {r.gradoNombre} {r.seccionNombre} — DNI {r.estudianteDni}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border ${ESTADO_DIARIA_COLOR[r.estado]}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${ESTADO_DIARIA_DOT[r.estado]}`} />
                    {ESTADO_DIARIA_LABEL[r.estado]}
                  </span>
                  {r.horaIngreso && (
                    <p className="text-xs text-slate-500 mt-1 tabular-nums">{r.horaIngreso.slice(0, 5)}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ── Card del estudiante recién registrado ─────────────────────

const CardEstudiante: React.FC<{ reg: AsistenciaDiariaResponse }> = ({ reg }) => {
  const esPresente = reg.estado === 'PRESENTE';
  const colorBg = esPresente ? 'from-green-50 to-emerald-50 border-green-200' : 'from-amber-50 to-orange-50 border-amber-200';
  const colorBadge = esPresente ? 'bg-green-600 text-white' : 'bg-amber-500 text-white';

  return (
    <div className={`bg-gradient-to-br ${colorBg} border rounded-2xl p-5 flex items-center gap-5`}>
      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center font-extrabold text-xl flex-shrink-0
                       ${esPresente ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
        {reg.estudianteNombres.charAt(0)}{reg.estudianteApellidos.charAt(0)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-lg font-extrabold text-slate-800 leading-tight">
          {reg.estudianteApellidos}, {reg.estudianteNombres}
        </p>
        <p className="text-sm text-slate-500 mt-0.5">
          DNI {reg.estudianteDni} · {reg.gradoNombre} {reg.seccionNombre}
        </p>
        <div className="flex items-center gap-2 mt-2">
          <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${NIVEL_COLOR[reg.nivel] ?? 'bg-slate-100 text-slate-600'}`}>
            {reg.nivel}
          </span>
          {reg.horaIngreso && (
            <span className="text-xs font-bold text-slate-600 tabular-nums">
              <I.Clock size={11} style={{ display: 'inline-block', marginRight: 4, verticalAlign: -1 }} />
              {reg.horaIngreso.slice(0, 5)}
            </span>
          )}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${colorBadge}`}>
          <I.CheckCircle size={14} />
          {ESTADO_DIARIA_LABEL[reg.estado]}
        </span>
      </div>
    </div>
  );
};

// ── Contador del día ──────────────────────────────────────────

const ContadorDia: React.FC<{ presentes: number; tardanzas: number; total: number }> = ({ presentes, tardanzas, total }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
    <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-4">Resumen de hoy</p>
    <div className="grid gap-3">
      <div className="bg-green-50 rounded-xl px-4 py-3 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-green-700 opacity-70">Presentes</p>
          <p className="text-2xl font-extrabold text-green-700 tabular-nums">{presentes}</p>
        </div>
        <I.CheckCircle size={24} className="text-green-600" />
      </div>
      <div className="bg-amber-50 rounded-xl px-4 py-3 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-amber-700 opacity-70">Tardanzas</p>
          <p className="text-2xl font-extrabold text-amber-700 tabular-nums">{tardanzas}</p>
        </div>
        <I.Clock size={24} className="text-amber-600" />
      </div>
      <div className="bg-slate-50 rounded-xl px-4 py-3 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-slate-500 opacity-70">Total registrados</p>
          <p className="text-2xl font-extrabold text-slate-700 tabular-nums">{total}</p>
        </div>
        <I.Users size={24} className="text-slate-500" />
      </div>
    </div>
  </div>
);

export default PorteriaPage;
